/**
 * n8n Workflow Converter for Tasking.tech
 * Converts n8n workflow JSON format to Tasking.tech workflow format
 */

import {
  WorkflowNode,
  WorkflowNodeType,
  WorkflowDefinition,
  WorkflowConnection,
} from './workflowTypes';

// n8n node types mapping to Tasking.tech types
const N8N_TYPE_MAPPING: Record<string, WorkflowNodeType> = {
  // Triggers
  'n8n-nodes-base.webhook': 'trigger_webhook',
  'n8n-nodes-base.scheduleTrigger': 'trigger_schedule',
  'n8n-nodes-base.manualTrigger': 'trigger_manual',
  'n8n-nodes-base.start': 'trigger_manual',
  'n8n-nodes-base.cron': 'trigger_schedule',

  // HTTP/API
  'n8n-nodes-base.httpRequest': 'action_http',
  'n8n-nodes-base.httpRequestTool': 'action_http',
  'n8n-nodes-base.respondToWebhook': 'action_http',

  // Code execution
  'n8n-nodes-base.code': 'action_code',
  'n8n-nodes-base.function': 'action_code',
  'n8n-nodes-base.functionItem': 'action_code',
  'n8n-nodes-base.executeCommand': 'action_code',

  // Messaging
  'n8n-nodes-base.slack': 'action_slack',
  'n8n-nodes-base.slackTool': 'action_slack',
  'n8n-nodes-base.discord': 'action_discord',
  'n8n-nodes-base.emailSend': 'action_email',
  'n8n-nodes-base.sendEmail': 'action_email',
  'n8n-nodes-base.gmail': 'action_email',
  'n8n-nodes-base.microsoftOutlook': 'action_email',

  // AI
  'n8n-nodes-base.openAi': 'action_ai',
  '@n8n/n8n-nodes-langchain.openAi': 'action_ai',
  '@n8n/n8n-nodes-langchain.lmChatOpenAi': 'action_ai',
  '@n8n/n8n-nodes-langchain.agent': 'action_ai',
  '@n8n/n8n-nodes-langchain.chainLlm': 'action_ai',
  'n8n-nodes-base.anthropic': 'action_ai',

  // Database
  'n8n-nodes-base.postgres': 'action_database',
  'n8n-nodes-base.mysql': 'action_database',
  'n8n-nodes-base.mongodb': 'action_database',
  'n8n-nodes-base.redis': 'action_database',
  'n8n-nodes-base.airtable': 'action_database',
  'n8n-nodes-base.googleSheets': 'action_database',
  'n8n-nodes-base.notion': 'action_database',

  // Logic/Flow
  'n8n-nodes-base.if': 'logic_if',
  'n8n-nodes-base.switch': 'logic_switch',
  'n8n-nodes-base.splitInBatches': 'logic_loop',
  'n8n-nodes-base.wait': 'logic_delay',
  'n8n-nodes-base.merge': 'logic_parallel',
  'n8n-nodes-base.noOp': 'logic_delay',
  'n8n-nodes-base.set': 'action_code',
  'n8n-nodes-base.filter': 'logic_if',
};

// Interface for n8n workflow format
export interface N8nNode {
  id: string;
  name: string;
  type: string;
  typeVersion?: number;
  position: [number, number];
  parameters?: Record<string, any>;
  credentials?: Record<string, any>;
  notes?: string;
}

export interface N8nConnection {
  node: string;
  type: string;
  index: number;
}

export interface N8nWorkflow {
  name: string;
  description?: string;
  nodes: N8nNode[];
  connections: Record<string, Record<string, N8nConnection[][]>>;
  settings?: Record<string, any>;
  meta?: Record<string, any>;
  tags?: string[];
}

/**
 * Maps n8n node type to Tasking.tech node type
 */
function mapNodeType(n8nType: string): WorkflowNodeType {
  // Check direct mapping first
  if (N8N_TYPE_MAPPING[n8nType]) {
    return N8N_TYPE_MAPPING[n8nType];
  }

  // Fuzzy matching for common patterns
  const typeLower = n8nType.toLowerCase();

  if (typeLower.includes('trigger') || typeLower.includes('start')) {
    if (typeLower.includes('webhook')) return 'trigger_webhook';
    if (typeLower.includes('schedule') || typeLower.includes('cron')) return 'trigger_schedule';
    return 'trigger_manual';
  }

  if (typeLower.includes('http') || typeLower.includes('request') || typeLower.includes('api')) {
    return 'action_http';
  }

  if (typeLower.includes('slack')) return 'action_slack';
  if (typeLower.includes('discord')) return 'action_discord';
  if (typeLower.includes('email') || typeLower.includes('mail') || typeLower.includes('gmail'))
    return 'action_email';

  if (
    typeLower.includes('openai') ||
    typeLower.includes('ai') ||
    typeLower.includes('llm') ||
    typeLower.includes('gpt') ||
    typeLower.includes('anthropic') ||
    typeLower.includes('agent')
  ) {
    return 'action_ai';
  }

  if (
    typeLower.includes('database') ||
    typeLower.includes('postgres') ||
    typeLower.includes('mysql') ||
    typeLower.includes('mongo') ||
    typeLower.includes('airtable') ||
    typeLower.includes('sheet') ||
    typeLower.includes('notion')
  ) {
    return 'action_database';
  }

  if (typeLower.includes('if') || typeLower.includes('filter') || typeLower.includes('condition')) {
    return 'logic_if';
  }

  if (typeLower.includes('switch') || typeLower.includes('route')) {
    return 'logic_switch';
  }

  if (typeLower.includes('loop') || typeLower.includes('batch') || typeLower.includes('iterate')) {
    return 'logic_loop';
  }

  if (typeLower.includes('wait') || typeLower.includes('delay') || typeLower.includes('sleep')) {
    return 'logic_delay';
  }

  if (
    typeLower.includes('code') ||
    typeLower.includes('function') ||
    typeLower.includes('script') ||
    typeLower.includes('set') ||
    typeLower.includes('transform')
  ) {
    return 'action_code';
  }

  // Default fallback
  return 'action_http';
}

/**
 * Converts n8n node parameters to Tasking.tech config
 */
function convertNodeConfig(n8nNode: N8nNode, taskingType: WorkflowNodeType): Record<string, any> {
  const params = n8nNode.parameters || {};
  const config: Record<string, any> = {};

  switch (taskingType) {
    case 'trigger_webhook':
      config.webhookPath = params.path || params.webhookPath || '/webhook';
      break;

    case 'trigger_schedule':
      config.cronExpression = params.cronExpression || params.rule?.interval || '0 * * * *';
      break;

    case 'action_http':
      config.method = params.method || params.requestMethod || 'GET';
      config.url = params.url || params.endpoint || '';
      if (params.headers) {
        config.headers =
          typeof params.headers === 'string' ? params.headers : JSON.stringify(params.headers);
      }
      if (params.body || params.bodyParameters) {
        config.body =
          typeof params.body === 'string'
            ? params.body
            : JSON.stringify(params.body || params.bodyParameters);
      }
      break;

    case 'action_code':
      config.code =
        params.code ||
        params.functionCode ||
        params.jsCode ||
        '// Converted from n8n\nreturn input;';
      config.language = 'javascript';
      break;

    case 'action_ai':
      config.prompt = params.prompt || params.text || params.messages?.[0]?.content || '';
      config.model = params.model || 'llama3.3:70b';
      break;

    case 'action_slack':
    case 'action_discord':
      config.webhookUrl = params.webhookUrl || '';
      config.message = params.text || params.message || params.content || '';
      break;

    case 'action_email':
      config.to = params.toEmail || params.to || '';
      config.subject = params.subject || '';
      config.bodyTemplate = params.message || params.body || params.text || '';
      break;

    case 'action_database':
      config.operation = params.operation || 'query';
      config.table = params.table || params.tableName || '';
      config.query = params.query || params.sql || '';
      break;

    case 'logic_if':
      config.condition = params.conditions?.rules?.[0]?.value1
        ? `input.${params.conditions.rules[0].value1} ${params.conditions.rules[0].operation || '=='} ${params.conditions.rules[0].value2}`
        : 'true';
      break;

    case 'logic_switch':
      config.condition = params.value1 || 'input.value';
      config.cases =
        params.rules?.map((r: any, i: number) => ({
          value: r.value || `case${i}`,
          label: r.outputKey || `Case ${i + 1}`,
        })) || [];
      break;

    case 'logic_delay':
      config.delayMs = (params.amount || params.time || 1) * 1000;
      break;
  }

  // Preserve original n8n type for reference
  config._originalN8nType = n8nNode.type;
  config._originalName = n8nNode.name;

  return config;
}

/**
 * Auto-layout nodes in a left-to-right graph using topological layers.
 * Distributes nodes across the canvas so they're centered and visible.
 */
function autoLayoutNodes(nodes: WorkflowNode[], connections: WorkflowConnection[]): void {
  if (nodes.length === 0) return;

  const NODE_WIDTH = 200;
  const NODE_HEIGHT = 80;
  const H_GAP = 100; // horizontal gap between columns
  const V_GAP = 40;  // vertical gap between rows
  const PADDING_X = 60;
  const PADDING_Y = 60;

  // Build adjacency: sourceId -> targetIds
  const children = new Map<string, string[]>();
  const parents = new Map<string, string[]>();
  for (const node of nodes) {
    children.set(node.id, []);
    parents.set(node.id, []);
  }
  for (const conn of connections) {
    children.get(conn.sourceNodeId)?.push(conn.targetNodeId);
    parents.get(conn.targetNodeId)?.push(conn.sourceNodeId);
  }

  // Assign layers via BFS from roots (nodes with no parents)
  const layer = new Map<string, number>();
  const roots = nodes.filter((n) => (parents.get(n.id)?.length || 0) === 0);
  // If no roots found (cycle or disconnected), treat all as roots
  const startNodes = roots.length > 0 ? roots : nodes;

  const queue: string[] = [];
  for (const root of startNodes) {
    layer.set(root.id, 0);
    queue.push(root.id);
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentLayer = layer.get(current) || 0;
    for (const childId of children.get(current) || []) {
      const existingLayer = layer.get(childId);
      if (existingLayer === undefined || existingLayer < currentLayer + 1) {
        layer.set(childId, currentLayer + 1);
        queue.push(childId);
      }
    }
  }

  // Assign layer 0 to any unvisited nodes
  for (const node of nodes) {
    if (!layer.has(node.id)) layer.set(node.id, 0);
  }

  // Group nodes by layer
  const layers = new Map<number, WorkflowNode[]>();
  for (const node of nodes) {
    const l = layer.get(node.id) || 0;
    if (!layers.has(l)) layers.set(l, []);
    layers.get(l)!.push(node);
  }

  const maxLayer = Math.max(...layers.keys());

  // Determine the max nodes in any single layer (for row height calculation)
  let maxNodesInLayer = 1;
  for (let l = 0; l <= maxLayer; l++) {
    maxNodesInLayer = Math.max(maxNodesInLayer, (layers.get(l) || []).length);
  }

  // Calculate how many columns fit before wrapping into a new row.
  // Aim for a roughly square aspect ratio by limiting columns.
  const totalLayers = maxLayer + 1;
  const colWidth = NODE_WIDTH + H_GAP;
  const rowHeight = maxNodesInLayer * (NODE_HEIGHT + V_GAP) + V_GAP * 3; // extra gap between rows

  // Target: roughly sqrt(totalLayers) columns so the layout is squarish
  const maxCols = Math.max(3, Math.ceil(Math.sqrt(totalLayers * 1.5)));

  // Position each layer, wrapping into rows
  for (let l = 0; l <= maxLayer; l++) {
    const layerNodes = layers.get(l) || [];
    const col = l % maxCols;
    const row = Math.floor(l / maxCols);

    for (let i = 0; i < layerNodes.length; i++) {
      layerNodes[i].position = {
        x: PADDING_X + col * colWidth,
        y: PADDING_Y + row * rowHeight + i * (NODE_HEIGHT + V_GAP),
      };
    }
  }
}

/**
 * Converts n8n workflow to Tasking.tech format
 */
export function convertN8nWorkflow(n8nWorkflow: N8nWorkflow): WorkflowDefinition {
  const nodes: WorkflowNode[] = [];
  const connections: WorkflowConnection[] = [];
  const nodeIdMap = new Map<string, string>(); // n8n node name -> Tasking.tech node id

  // Convert nodes
  for (const n8nNode of n8nWorkflow.nodes) {
    const taskingType = mapNodeType(n8nNode.type);
    const nodeId = crypto.randomUUID();

    nodeIdMap.set(n8nNode.name, nodeId);

    const taskingNode: WorkflowNode = {
      id: nodeId,
      type: taskingType,
      name: n8nNode.name,
      position: {
        x: n8nNode.position[0],
        y: n8nNode.position[1],
      },
      config: convertNodeConfig(n8nNode, taskingType),
    };

    nodes.push(taskingNode);
  }

  // Convert connections
  for (const [sourceName, outputs] of Object.entries(n8nWorkflow.connections)) {
    const sourceId = nodeIdMap.get(sourceName);
    if (!sourceId) continue;

    for (const [outputType, outputConnections] of Object.entries(outputs)) {
      for (let outputIndex = 0; outputIndex < outputConnections.length; outputIndex++) {
        const connectionsAtIndex = outputConnections[outputIndex];
        if (!connectionsAtIndex) continue;

        for (const conn of connectionsAtIndex) {
          const targetId = nodeIdMap.get(conn.node);
          if (!targetId) continue;

          const connection: WorkflowConnection = {
            id: crypto.randomUUID(),
            sourceNodeId: sourceId,
            sourceHandle: outputIndex === 0 ? 'output' : `output_${outputIndex}`,
            targetNodeId: targetId,
            targetHandle: 'input',
          };

          connections.push(connection);
        }
      }
    }
  }

  // Auto-layout nodes so they use the full canvas space
  autoLayoutNodes(nodes, connections);

  return {
    id: crypto.randomUUID(),
    name: n8nWorkflow.name || 'Imported Workflow',
    description: n8nWorkflow.description || `Imported from n8n`,
    nodes,
    connections,
    variables: {},
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Parses and validates n8n workflow JSON
 */
export function parseN8nWorkflowJson(jsonString: string): N8nWorkflow {
  const parsed = JSON.parse(jsonString);

  // Validate required fields
  if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
    throw new Error('Invalid n8n workflow: missing nodes array');
  }

  // Ensure connections exist
  if (!parsed.connections) {
    parsed.connections = {};
  }

  return parsed as N8nWorkflow;
}

/**
 * Batch converts multiple n8n workflows
 */
export function convertMultipleN8nWorkflows(workflows: N8nWorkflow[]): WorkflowDefinition[] {
  return workflows.map(convertN8nWorkflow);
}

/**
 * Fetches n8n workflow from URL
 */
export async function fetchN8nWorkflowFromUrl(url: string): Promise<N8nWorkflow> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch workflow: ${response.statusText}`);
  }
  const text = await response.text();
  return parseN8nWorkflowJson(text);
}

/**
 * Gets statistics about an n8n workflow for preview
 */
export function getN8nWorkflowStats(workflow: N8nWorkflow): {
  nodeCount: number;
  connectionCount: number;
  nodeTypes: Record<string, number>;
  hasTrigger: boolean;
} {
  const nodeTypes: Record<string, number> = {};
  let connectionCount = 0;
  let hasTrigger = false;

  for (const node of workflow.nodes) {
    const taskingType = mapNodeType(node.type);
    nodeTypes[taskingType] = (nodeTypes[taskingType] || 0) + 1;

    if (taskingType.startsWith('trigger_')) {
      hasTrigger = true;
    }
  }

  for (const outputs of Object.values(workflow.connections)) {
    for (const outputConnections of Object.values(outputs)) {
      for (const connectionsAtIndex of outputConnections) {
        connectionCount += connectionsAtIndex?.length || 0;
      }
    }
  }

  return {
    nodeCount: workflow.nodes.length,
    connectionCount,
    nodeTypes,
    hasTrigger,
  };
}
